import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Tags,
  Package
} from 'lucide-react';
import toast from 'react-hot-toast';
import ConfirmModal from '../../components/ui/ConfirmModal';

const AdminCategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, categoryId: null });
  const [formData, setFormData] = useState({
    name_fr: '',
    name_en: '',
    description_fr: '',
    description_en: '',
    sort_order: 0,
    is_active: true
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/admin/categories', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories);
      } else {
        toast.error('Erreur lors du chargement des catégories');
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Erreur lors du chargement des catégories');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/admin/categories', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Catégorie créée avec succès');
        setShowCreateModal(false);
        resetForm();
        fetchCategories();
      } else {
        const error = await response.json();
        toast.error(error.message || 'Erreur lors de la création');
      }
    } catch (error) {
      console.error('Error creating category:', error);
      toast.error('Erreur lors de la création');
    }
  };

  const handleUpdateCategory = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/admin/categories/${editingCategory.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Catégorie mise à jour avec succès');
        setShowEditModal(false);
        setEditingCategory(null);
        resetForm();
        fetchCategories();
      } else {
        const error = await response.json();
        toast.error(error.message || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/admin/categories/${categoryId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Catégorie supprimée avec succès');
        fetchCategories();
      } else {
        const error = await response.json();
        toast.error(error.message || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const openEditModal = (category) => {
    setEditingCategory(category);
    setFormData({
      name_fr: category.name_fr || '',
      name_en: category.name_en || '',
      description_fr: category.description_fr || '',
      description_en: category.description_en || '',
      sort_order: category.sort_order || 0,
      is_active: category.is_active === 1
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name_fr: '',
      name_en: '',
      description_fr: '',
      description_en: '',
      sort_order: 0,
      is_active: true
    });
  };

  const getStatusBadge = (isActive) => (
    <span className={`inline-flex items-center justify-center text-center min-w-[90px] px-2.5 py-0.5 rounded-full text-xs font-medium ${
      isActive 
        ? 'bg-green-100 text-green-800' 
        : 'bg-red-100 text-red-800'
    }`}>
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );

  const filteredCategories = categories.filter(category =>
    category.name_fr.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.name_en.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">Gestion des Catégories</h1>
          <p className="text-gray-400 mt-2">Organisez votre catalogue de bijoux TOLOU</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold px-4 py-2 rounded-lg transition-colors duration-200 flex items-center space-x-2"
        >
          <Plus size={16} />
          <span>Nouvelle Catégorie</span>
        </button>
      </div>

      {/* Search */}
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
        <div className="max-w-md">
          <label className="block text-sm font-medium text-gray-300 mb-2">Rechercher</label>
          <div className="relative">
            <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Nom de catégorie..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCategories.map((category) => (
          <motion.div
            key={category.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-gray-600 transition-colors"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg flex items-center justify-center">
                <Tags size={24} className="text-white" />
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => openEditModal(category)}
                  className="text-blue-400 hover:text-blue-300 transition-colors p-1"
                >
                  <Edit size={16} />
                </button>
                <button
                  onClick={() => setConfirmDelete({ open: true, categoryId: category.id })}
                  className="text-red-400 hover:text-red-300 transition-colors p-1"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <h3 className="text-lg font-semibold text-white">{category.name_fr}</h3>
                {category.name_en && (
                  <p className="text-gray-400 text-sm">{category.name_en}</p>
                )}
              </div>

              {category.description_fr && (
                <p className="text-gray-300 text-sm line-clamp-2">
                  {category.description_fr}
                </p>
              )}

              <div className="flex items-center justify-between pt-3 border-t border-gray-700">
                <div className="flex items-center space-x-2">
                  <Package size={16} className="text-gray-400" />
                  <span className="text-gray-300 text-sm">
                    {category.product_count || 0} produit{category.product_count !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  {getStatusBadge(category.is_active)}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {filteredCategories.length === 0 && (
        <div className="text-center py-12">
          <Tags size={48} className="text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-400 mb-2">
            {searchTerm ? 'Aucune catégorie trouvée' : 'Aucune catégorie'}
          </h3>
          <p className="text-gray-500">
            {searchTerm 
              ? 'Essayez de modifier vos critères de recherche'
              : 'Commencez par créer votre première catégorie'
            }
          </p>
        </div>
      )}

      {/* Create Category Modal */}
      {showCreateModal && (
        <CategoryModal
          title="Créer une nouvelle catégorie"
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleCreateCategory}
          onClose={() => {
            setShowCreateModal(false);
            resetForm();
          }}
        />
      )}

      {/* Edit Category Modal */}
      {showEditModal && (
        <CategoryModal
          title="Modifier la catégorie"
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleUpdateCategory}
          onClose={() => {
            setShowEditModal(false);
            setEditingCategory(null);
            resetForm();
          }}
        />
      )}

      {/* Confirm Delete Modal */}
      <ConfirmModal
        isOpen={confirmDelete.open}
        title="Supprimer la catégorie"
        message="Êtes-vous sûr de vouloir supprimer cette catégorie ? Cette action est irréversible."
        confirmText="Supprimer"
        cancelText="Annuler"
        danger
        onCancel={() => setConfirmDelete({ open: false, categoryId: null })}
        onConfirm={async () => {
          const id = confirmDelete.categoryId;
          setConfirmDelete({ open: false, categoryId: null });
          await handleDeleteCategory(id);
        }}
      />
    </div>
  );
};

// Category Modal Component
const CategoryModal = ({ title, formData, setFormData, onSubmit, onClose }) => {
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-gray-800 rounded-xl max-w-2xl w-full">
        <div className="p-6 border-b border-gray-700">
          <h2 className="text-2xl font-bold text-white">{title}</h2>
        </div>
        
        <form onSubmit={onSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Nom (Français)</label>
              <input
                type="text"
                name="name_fr"
                value={formData.name_fr}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Nom (Anglais)</label>
              <input
                type="text"
                name="name_en"
                value={formData.name_en}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Description (FR)</label>
              <textarea
                name="description_fr"
                value={formData.description_fr}
                onChange={handleChange}
                rows="3"
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Description (EN)</label>
              <textarea
                name="description_en"
                value={formData.description_en}
                onChange={handleChange}
                rows="3"
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Ordre de tri</label>
              <input
                type="number"
                name="sort_order"
                value={formData.sort_order}
                onChange={handleChange}
                min="0"
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              />
            </div>

            <div className="flex items-end">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="h-4 w-4 text-yellow-500 focus:ring-yellow-500 border-gray-600 rounded bg-gray-700"
                />
                <span className="ml-2 text-gray-300">Catégorie active</span>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-yellow-500 text-gray-900 font-semibold rounded-lg hover:bg-yellow-600 transition-colors"
            >
              {title.includes('Créer') ? 'Créer' : 'Mettre à jour'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminCategoriesPage; 